export function getDashboardKey(data) {
  return `${data?.store_id}-${data?.timeframe_start}-${data?.timeframe_start}-${
    data?.is_live
  }-${data?.store_dashboard_types.join("-")}`;
}

export function capitalizeText(text) {
  return text
    ?.toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * Format a date to a standard readable format
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date like "Oct 9, 2025, 7:08 AM" or "-" if invalid
 */
export const formatDateTime = (date) => {
  if (!date) return "-";
  try {
    const inputDate = new Date(date);

    // Check if date is valid
    if (isNaN(inputDate.getTime())) return "-";

    const options = {
      year: "numeric",
      month: "short", // Use short month format (Oct instead of October)
      day: "numeric",
      hour: "numeric", // Don't pad hours with leading zero
      minute: "2-digit",
      hour12: true,
    };

    return inputDate.toLocaleString("en-US", options);
  } catch (e) {
    return "-";
  }
};

/**
 * Get the appropriate vue-feather icon type based on file extension from URL
 * @param {string} urlPath - URL or file path containing the file extension
 * @returns {string} Icon type name for vue-feather component
 */
export function getFileIcon(urlPath) {
  if (!urlPath) return "file";
  const url = urlPath.toLowerCase();
  // Extract file extension
  const match = url.match(/\.([a-z0-9]+)(?:\?|$)/);
  const ext = match ? match[1] : "";

  // Map extensions to vue-feather icon types
  // Distinguishing PDF (file-text), DOC/DOCX (file), and XLS/XLSX (layout for spreadsheets)
  const iconMap = {
    pdf: "file-text",
    doc: "file",
    docx: "file",
    xls: "layout",
    xlsx: "layout",
    csv: "layout",
    ppt: "file",
    pptx: "file",
    png: "image",
    jpg: "image",
    jpeg: "image",
    gif: "image",
    webp: "image",
    svg: "image",
    txt: "file-text",
    zip: "archive",
    rar: "archive",
    "7z": "archive",
  };

  return iconMap[ext] || "file";
}

export function formatNumber(value) {
  const num = parseFloat(value);

  // Check if the value has any decimal part after two places
  if (num % 1 === 0) {
    return num.toString(); // Return as a whole number if no decimals
  }

  // Convert to string and ensure two decimal places
  let formatted = num.toFixed(2);

  // Remove trailing zero if present after decimal
  if (formatted.endsWith("0")) {
    formatted = parseFloat(formatted).toString(); // Convert back to a number and remove unnecessary zeros
  }

  return formatted;
}

export function formatTableHeader(key) {
  return key
    .replace(/_/g, " ") // Replace underscores with spaces
    .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize the first letter of each word
}

// Utility function to revert formatted header text back to the original key
export function revertFormattedHeader(formattedHeader) {
  return formattedHeader
    .replace(/\s+/g, "_") // Replace spaces with underscores
    .toLowerCase(); // Convert to lowercase
}

// Function to generate headers based on the input array of objects
export function generateTableHeadersFromJson(arrOfObj) {
  if (!Array.isArray(arrOfObj) || arrOfObj.length === 0) {
    return []; // Return an empty array if input is not valid
  }

  const firstItem = arrOfObj[0];
  return Object.keys(firstItem)
    .filter((key) => typeof firstItem[key] !== "number") // Exclude keys with number values
    .map((key) => formatTableHeader(key)); // Format the keys
}

// Function to generate a mapping of formatted headers to original keys
export function generateTableHeaderKeyMappingFromJson(arrOfObj) {
  const headers = generateTableHeadersFromJson(arrOfObj); // Generate headers
  return headers.reduce((acc, header) => {
    const originalKey = revertFormattedHeader(header); // Get the original key
    acc[header] = originalKey; // Map formatted header to original key
    return acc; // Return the accumulated mapping
  }, {});
}

/**
 * Convert GMT/UTC date to local time with standard formatting
 * @param {string|Date} date - Date to convert and format
 * @returns {string} Formatted date like "Oct 9, 2025, 7:08 AM" or the original value if invalid
 */
export function convertGMTToLocalTime(date) {
  if (!date) return "-";
  try {
    const inputDate = new Date(date);

    // Check if date is valid
    if (isNaN(inputDate.getTime())) return date;

    return inputDate.toLocaleString("en-US", {
      year: "numeric",
      month: "short", // Use short month format (Oct instead of October)
      day: "numeric",
      hour: "numeric", // Don't pad hours with leading zero
      minute: "2-digit",
      hour12: true,
    });
  } catch (e) {
    return date;
  }
}

/**
 * Format just the date portion (no time)
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date like "Oct 9, 2025" or "-" if invalid
 */
export function formatDate(date) {
  if (!date) return "-";
  try {
    const inputDate = new Date(date);

    // Check if date is valid
    if (isNaN(inputDate.getTime())) return "-";

    return inputDate.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch (e) {
    return "-";
  }
}

export function safeJSONParse(str, fallback = null) {
  try {
    return JSON.parse(str);
  } catch {
    return fallback;
  }
}

/**
 * Format seconds into a human-readable time string
 * @param {number} seconds - Number of seconds
 * @returns {string} Formatted string like "in 1 hour", "in 2 hours and 30 minutes", etc.
 */
export function formatRetryTime(seconds) {
  if (!seconds || seconds <= 0) {
    return "";
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  const parts = [];

  if (hours > 0) {
    parts.push(`${hours} hour${hours > 1 ? "s" : ""}`);
  }

  if (minutes > 0) {
    parts.push(`${minutes} minute${minutes > 1 ? "s" : ""}`);
  }

  // Only include seconds if less than 1 hour and no minutes
  if (hours === 0 && minutes === 0 && remainingSeconds > 0) {
    parts.push(`${remainingSeconds} second${remainingSeconds > 1 ? "s" : ""}`);
  }

  if (parts.length === 0) {
    return "";
  }

  if (parts.length === 1) {
    return `in ${parts[0]}`;
  }

  // Join with "and" for the last part
  const lastPart = parts.pop();
  return `in ${parts.join(", ")} and ${lastPart}`;
}

/**
 * Extract and format error message from API error response
 * Handles rate limit errors (429) with retry_after_seconds
 * @param {Error} error - Axios error object
 * @returns {string} Formatted error message
 */
export function formatErrorMessage(error) {
  if (!error) {
    return "An unexpected error occurred";
  }

  const responseData = error?.response?.data;
  const errorData = responseData?.error || responseData;
  const status = error?.response?.status;

  // Handle rate limit errors (429)
  if (status === 429) {
    const retryAfter = errorData?.retry_after_seconds;
    const baseMessage =
      errorData?.message || "Rate limit exceeded. Please try again later.";

    if (retryAfter) {
      const retryTime = formatRetryTime(retryAfter);
      return retryTime ? `${baseMessage} You can retry ${retryTime}.` : baseMessage;
    }

    return baseMessage;
  }

  // Handle other errors
  return (
    errorData?.message ||
    responseData?.message ||
    error?.message ||
    "An unexpected error occurred"
  );
}

export function safeJSONStringify(obj, fallback = null) {
  try {
    return JSON.stringify(obj);
  } catch {
    return fallback;
  }
}
